import type { ComponentProps } from 'react';

import { Autocomplete, FormControl } from '@ifs-gcp/components';
import { AnswerTypes } from '@ifs-gcp/core/models';

import { useAuditStore } from '@/store/auditStore';
import type { AnswerProps } from '@/types/question';
import { getTranslation } from '@/utils/translations';

type AutocompleteOptionType = ComponentProps<typeof Autocomplete>['options'][number];

export const AutocompleteAnswer = ({
  error,
  answer,
  onBlur,
  onChange,
  language,
  question,
}: AnswerProps<string[]>) => {
  const {
    props: { id: questionId, options = [], variant, disabledCondition, readOnlyCondition },
  } = question;

  const audit = useAuditStore((state) => state.audit)!;

  /**
   * For multiselect we pass all values from the array
   * while single selects only use the first element from the array
   * In previous implementations the value could be string | string[]
   * but now single selects are saved as an array with one value
   */
  const autoCompleteValue = variant === AnswerTypes.MULTI_SELECT ? answer : answer[0];

  const getAutocompleteOptions = () => {
    const mainOptions = options
      .filter((option) => {
        if ('visibilityCondition' in option) {
          return audit.conditions.validate(option.visibilityCondition);
        }

        return true;
      })
      .map<AutocompleteOptionType>(({ value: optionValue, text }) => ({
        value: optionValue,
        label: getTranslation(text, language),
      }));

    /**
     * If there is value selected, but not present in the options
     * because it got removed in definitions we have to cover that case
     */
    if (autoCompleteValue) {
      /**
       * Check single selects. The value is always string, and if it's not in the options
       * then we have to append a single value
       */
      if (
        typeof autoCompleteValue === 'string' &&
        !mainOptions.some(({ value: optionValue }) => optionValue === autoCompleteValue)
      ) {
        mainOptions.push({
          value: autoCompleteValue,
          label: autoCompleteValue,
        });
      }

      /**
       * Check if value comes from a multi select
       */
      if (Array.isArray(autoCompleteValue)) {
        /**
         * Filter current value for elements that are selected, but not in the options
         */
        const filteredOptions = autoCompleteValue.filter(
          (singleValue) =>
            !mainOptions.some(({ value: optionValue }) => optionValue === singleValue),
        );

        /**
         * Append selected elements to the options
         */
        if (filteredOptions.length) {
          mainOptions.push(
            ...filteredOptions.map<AutocompleteOptionType>((option) => ({
              value: option,
              label: option,
            })),
          );
        }
      }
    }

    return mainOptions;
  };

  return (
    <FormControl error={Boolean(error)} helperText={error}>
      <Autocomplete
        id={questionId}
        onBlur={(event) => onBlur?.(event)}
        // searchable={Boolean(searchText)}
        options={getAutocompleteOptions()}
        disabled={readOnlyCondition || disabledCondition}
        multiple={variant === AnswerTypes.MULTI_SELECT}
        value={autoCompleteValue}
        disablePortal={false}
        onChange={(_, data) => {
          if (Array.isArray(data)) {
            onChange(data.map(({ value: checkedValue }) => checkedValue));
          } else {
            onChange(data?.value ? [data.value] : []);
          }
        }}
      />
    </FormControl>
  );
};
